import { useState, useEffect } from 'react';
import { Modal, Radio, Space, Select, message } from 'antd';
import { changeConferenceHallDateApi } from '@services/Conference';
import dayjs from 'dayjs';
import './index.less';

const ChangeConferenceDateModal = props => {
    const { open, onCancel, conferenceHall, dateList, conferenceData, onSuccess } = props;

    const [selectedDate, setSelectedDate] = useState('');
    const [selectedParentHall, setSelectedParentHall] = useState(undefined);
    const [loading, setLoading] = useState(false);

    // 获取当前会议所在的日期
    const getCurrentDate = () => {
        if (!conferenceHall || !conferenceData) return '';

        // 从会议数据中找到当前会议所在的日期
        for (const date in conferenceData) {
            const halls = conferenceData[date];
            for (const hall of halls) {
                if (hall.id === conferenceHall.id) {
                    return date;
                }
                // 检查二级会议
                if (hall.secondHallsList && hall.secondHallsList.length > 0) {
                    for (const subHall of hall.secondHallsList) {
                        if (subHall.id === conferenceHall.id) {
                            return date;
                        }
                    }
                }
            }
        }
        return '';
    };

    // 获取可选的日期列表（排除当前日期）
    const getAvailableDates = () => {
        if (!dateList) return [];
        const currentDate = getCurrentDate();
        return dateList.filter(date => date !== currentDate);
    };

    // 获取指定日期下无日程的一级会议列表
    const getAvailableParentHalls = targetDate => {
        if (!targetDate || !conferenceData || !conferenceData[targetDate] || !conferenceHall) {
            return [];
        }

        // 从conferenceData中获取指定日期的会议列表
        const hallsInDate = conferenceData[targetDate];

        // 筛选无日程的一级会议（agendaNum === 0 且 pid === 0），排除当前会议自身
        const filteredHalls = hallsInDate.filter(hall => hall.agendaNum === 0 && hall.pid === 0 && hall.id !== conferenceHall.id);

        return filteredHalls;
    };

    // 重置状态
    const resetState = () => {
        setSelectedDate('');
        setSelectedParentHall(undefined);
        setLoading(false);
    };

    // 处理日期选择变化
    const handleDateChange = date => {
        setSelectedDate(date);
        setSelectedParentHall(undefined); // 重置所属会议选择
    };

    // 处理确认
    const handleConfirm = () => {
        if (!selectedDate) {
            message.warning('请选择新的会议日期');
            return;
        }

        if (!conferenceHall) {
            message.error('会议信息不完整，无法调整日期');
            return;
        }

        Modal.confirm({
            title: '确认调整会议日期',
            content: '确认调整后，请在新的会议日期中管理其下日程。',
            okText: '确认调整',
            cancelText: '取消',
            centered: true,
            onOk: async () => {
                setLoading(true);
                try {
                    const requestData = {
                        hallId: conferenceHall.id,
                        date: selectedDate,
                    };

                    // 如果选择了所属会议，添加到请求参数中
                    if (selectedParentHall) {
                        requestData.pid = selectedParentHall;
                    }

                    const res = await changeConferenceHallDateApi(requestData);

                    if (res.status_code === 200) {
                        message.success('调整会议日期成功');
                        onSuccess && onSuccess();
                        onCancel();
                    } else {
                        message.error(res.message || '调整会议日期失败');
                    }
                } catch (error) {
                    console.error('调整会议日期失败:', error);

                    // 处理HTTP 400错误
                    if (error.response?.status === 400) {
                        message.error('所属会议下有日程，不能调整至该会议下');
                    } else {
                        message.error('调整会议日期失败');
                    }
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // 当Modal打开时重置状态
    useEffect(() => {
        if (open) {
            resetState();
        }
    }, [open]);

    const availableDates = getAvailableDates();
    const currentDate = getCurrentDate();
    const availableParentHalls = getAvailableParentHalls(selectedDate);
    const showParentHallSelect = selectedDate && availableParentHalls.length > 0;

    // 如果没有会议信息，不渲染模态框内容
    if (!conferenceHall) {
        return (
            <Modal title="调整会议日期" open={open} onCancel={onCancel} footer={null} centered width={600}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ color: '#999' }}>会议信息加载中...</div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal title="调整会议日期" open={open} onCancel={onCancel} onOk={handleConfirm} confirmLoading={loading} centered okText="确定" cancelText="取消" width={600} className="change-conference-date-modal">
            <div className="field-section">
                <div className="field-label">当前会议：</div>
                <div className="conference-info">
                    <span>{conferenceHall?.name}</span>
                    <small className="conference-id">YHYC#{conferenceHall?.id}</small>
                </div>
            </div>

            <div className="field-section">
                <div className="field-label">当前会议日期：</div>
                <div className="field-value">{currentDate ? dayjs(currentDate).format('YYYY-MM-DD') : '未知'}</div>
            </div>

            <div className="field-section">
                <div className="field-label">调整到：</div>
                {availableDates.length > 0 ? (
                    <Radio.Group value={selectedDate} onChange={e => handleDateChange(e.target.value)} className="date-radio-group">
                        <Space orientation="vertical">
                            {availableDates.map(date => (
                                <Radio key={date} value={date}>
                                    {dayjs(date).format('YYYY-MM-DD')}
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                ) : (
                    <div className="no-data">暂无可选日期</div>
                )}
            </div>

            {showParentHallSelect && (
                <div className="field-section">
                    <div className="field-label">所属会议：</div>
                    <div className="field-description">请选择上级会议（仅限无日程的一级会议），如为一级会议则留空</div>
                    <Select className="parent-hall-select" placeholder="选择上级会议" value={selectedParentHall} onChange={setSelectedParentHall} allowClear>
                        {availableParentHalls.map(hall => (
                            <Select.Option key={hall.id} value={hall.id}>
                                {hall.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            )}
        </Modal>
    );
};

export default ChangeConferenceDateModal;
